/* ⛔ LA PONDERATION DU SCORE DU MODULE (glbMaster), LUE ET ECRITE DEPUIS LA
   SLIDE « FIN DE MODULE » (07/10/2026, fiche Notion de Nawal, partie B,
   « solution mixte »).

   LES DONNEES NE CHANGENT PAS DE PLACE. La ponderation reste le reglage du
   MODULE, `glbMasterPond` (tableau JSON de { key, value: "oui" | "non" }),
   celui du formulaire « glbMaster pondération » (ContentForm.js). La slide ne
   porte aucune copie : cocher une case dans la slide ecrit dans le module, et
   ce que le formulaire ecrit se voit dans la slide.

   MEME CHEMIN QUE LE FORMULAIRE, et c'est verifie par un test
   (ponderation-module.test.js, « parite avec le formulaire ») :
     1. le module est relu comme le formulaire le lit
        (`APIS.getContentByIdForAdmin`), pour partir de l'etat ENREGISTRE et
        non d'une copie de l'editeur qui pourrait dater ;
     2. la ponderation change comme dans le formulaire
        (`handleGlbMasterPondChange`) ;
     3. les MEMES valeurs partent par le MEME appel (`APIS.editContent`), avec
        les memes controles avant envoi (nom, surnom, description, type,
        duree).
   Seule difference, voulue : les soft skills partent TELLES QU'ENREGISTREES.
   Le formulaire, lui, retire au chargement celles qui n'ont plus de categorie
   et le signale (« Merci d'enregistrer ces modifications ») ; la slide ne
   decide pas cela a la place de Nawal.

   ⚠️ CE FICHIER NE CALCULE AUCUN SCORE. La regle « coche = compte » est celle
   du calcul (`retenuesParLaPonderation`, utils/scoreGlobal.js), importee et
   non recopiee. */
import APIS from './APIS'
import { getPointsByQuizz } from '../contexts/UserContext'
import { estCategorie, retenuesParLaPonderation } from './scoreGlobal'

export const OUI = 'oui'
export const NON = 'non'

/* `glbMasterPond` du module, en tableau. Absent, nul ou illisible : [] (rien
   de coche, comme le calcul). */
export const ponderationDuModule = (module) => {
  const brut = module?.glbMasterPond
  if (Array.isArray(brut)) return brut
  if (typeof brut !== 'string' || brut === '') return []
  try {
    const v = JSON.parse(brut)
    return Array.isArray(v) ? v : []
  } catch (e) {
    return []
  }
}

/* La case est cochee si la categorie compte dans le score du module : la
   regle du calcul, telle quelle. */
export const categorieCochee = (ponderation, key) => retenuesParLaPonderation(ponderation)(key)

/* Toutes les categories de TOUS les chapitres du module, dans l'ordre ou elles
   apparaissent, avec leurs points max : les questions notees (Quiz,
   Questionnaire), comme « Score par catégories » du Diagnostic et la
   ponderation du formulaire. « master » (categorie par defaut des questions) et
   « glbMaster » ne sont pas des categories : elles ne comptent dans aucun score
   de module, on ne les propose donc pas. */
export const categoriesDuModule = (module) => {
  const max = {}
  const ordre = []
  ;(module?.chapitre || []).forEach((ch) => (ch?.partie || []).forEach((pa) => (pa?.sequence || []).forEach((se) => {
    if (!se || !['Quizz', 'QuestionnaireV2'].includes(se.type)) return
    const cle = se.categorie
    if (!cle || !estCategorie(cle)) return
    const [, m] = Array.isArray(se.responses) ? getPointsByQuizz(se) : [0, 0]
    if (!(cle in max)) {
      max[cle] = 0
      ordre.push(cle)
    }
    max[cle] += Number.isFinite(m) ? m : 0
  })))
  return ordre.map((key) => ({ key, max: max[key] }))
}

/* Des questions classees « master » existent-elles ? (elles ne comptent dans
   aucun score : le panneau le dit) */
export const questionsSansCategorie = (module) => {
  let n = 0
  ;(module?.chapitre || []).forEach((ch) => (ch?.partie || []).forEach((pa) => (pa?.sequence || []).forEach((se) => {
    if (se && ['Quizz', 'QuestionnaireV2'].includes(se.type) && (!se.categorie || se.categorie === 'master')) n++
  })))
  return n
}

/* Au moins une categorie EXISTANTE du module compte : sinon, pas de score de
   module (fiche scoring du 07/10, sans repli). */
export const auMoinsUneCategorieCochee = (module) => {
  const pond = ponderationDuModule(module)
  return categoriesDuModule(module).some((c) => categorieCochee(pond, c.key))
}

/* ContentForm.handleGlbMasterPondChange, a l'identique : la ligne existante
   prend la nouvelle valeur, sinon une ligne s'ajoute. */
export const changerPonderation = (ponderation, key, value) => {
  const actuelle = Array.isArray(ponderation) ? ponderation : []
  if (actuelle.find((el) => el.key === key)) {
    return actuelle.map((s) => (s.key === key ? { key: s.key, value } : s))
  }
  return [...actuelle, { key, value }]
}

/* Les controles du formulaire avant envoi (ContentForm.onSubmitForm). */
export const champsDuModuleValides = (module) =>
  !!module
  && (module.name || '') !== ''
  && (module.surname || '') !== ''
  && (module.description || '') !== ''
  && module.type !== '' && module.type !== null && module.type !== undefined
  && !isNaN(parseInt(module.timing || 0))

/* Les valeurs que le formulaire envoie, pour ce module et cette ponderation.
   null si les soft skills enregistrees sont illisibles (le formulaire, lui,
   ne s'ouvrirait pas). */
export const valeursDuFormulaireDuModule = (module, glbMasterPond) => {
  let softSkills = []
  if (module?.softSkills !== null && module?.softSkills !== undefined) {
    try {
      softSkills = typeof module.softSkills === 'string' ? JSON.parse(module.softSkills) : module.softSkills
    } catch (e) {
      return null
    }
  }
  return {
    id: module.id,
    name: module.name || '',
    surname: module.surname || '',
    description: module.description || '',
    timing: parseInt(module.timing || 0),
    type: module.type,
    theme: module.theme,
    niveau: module.niveau,
    cible: module.cible,
    langue: module.langue,
    softSkills: JSON.stringify(softSkills),
    glbMasterPond: JSON.stringify(glbMasterPond),
  }
}

/* Coche (OUI) ou decoche (NON) une categorie, par le chemin du formulaire.
   Rend { ok: true, glbMasterPond (chaine enregistree), result, archive } ou
   { ok: false, raison } : `lecture` (module illisible), `champs` (le
   formulaire refuserait aussi : nom, surnom, description, type ou duree),
   `softSkills` (illisibles), `enregistrement` (le serveur n'a pas confirme).
   ⚠️ En cas d'echec, RIEN n'est change : ni le module, ni l'editeur. */
export const enregistrerPonderation = async ({ idModule, key, value, api = APIS }) => {
  const module = await api.getContentByIdForAdmin({ id: idModule })
  if (!module || typeof module !== 'object' || module.id === undefined || module.id === null) {
    return { ok: false, raison: 'lecture' }
  }
  if (!champsDuModuleValides(module)) return { ok: false, raison: 'champs' }
  const valeurs = valeursDuFormulaireDuModule(module, changerPonderation(ponderationDuModule(module), key, value))
  if (!valeurs) return { ok: false, raison: 'softSkills' }
  const result = await api.editContent(valeurs)
  if (!result || result.error) return { ok: false, raison: 'enregistrement' }
  return { ok: true, glbMasterPond: valeurs.glbMasterPond, result, archive: module.archived === 'true' }
}

/* Message a l'admin quand l'enregistrement n'a pas eu lieu (ecran d'admin,
   en francais comme le reste de l'editeur). */
export const MESSAGES_ECHEC = {
  lecture: "Le module n'a pas pu être relu. Rien n'a été modifié. Réessayez.",
  champs: "Le module a des informations incomplètes (nom, surnom, description, type ou durée)\u00a0: le formulaire du module refuserait aussi d'enregistrer. Complétez-les dans « Info sur le module ». Rien n'a été modifié.",
  softSkills: "Les soft skills du module sont illisibles\u00a0: ouvrez « Info sur le module » pour les vérifier. Rien n'a été modifié.",
  enregistrement: "La pondération n'a pas pu être enregistrée. Rien n'a été modifié. Réessayez.",
}
